// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/OfficesManager.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI } from "../../services/api";
import { usePermission } from "../../hooks/usePermission";
import { useSubmit } from "../../hooks/useSubmit";
import { Plus, Edit2, Trash2, Save, X, Building2 } from "lucide-react";

export default function OfficesManager() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("production", "add");
  const canEdit   = usePermission("production", "edit");
  const canDelete = usePermission("production", "delete");
  const { submitting, submit } = useSubmit();

  const [offices, setOffices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<any>(null);
  const [toast, setToast] = useState({ text: "", ok: true });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await trackingAPI.listOffices();
    if (r.success) setOffices(r.offices || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const handleAdd = async () => {
    if (!newName.trim()) { showToast("نام گمرک الزامی است", false); return; }
    const r = await trackingAPI.addOffice(newName.trim());
    if (r.success) { showToast("گمرک اضافه شد"); setNewName(""); setShowAdd(false); load(); }
    else showToast(r.message || "خطا", false);
  };

  const handleUpdate = async () => {
    if (!editing.name.trim()) { showToast("نام الزامی است", false); return; }
    const r = await trackingAPI.updateOffice(editing.id, editing.name.trim());
    if (r.success) { showToast("ویرایش شد"); setEditing(null); load(); }
    else showToast(r.message || "خطا", false);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`حذف گمرک "${name}"؟`)) return;
    const r = await trackingAPI.deleteOffice(id);
    if (r.success) { showToast("حذف شد"); load(); }
    else showToast(r.message || "خطا", false);
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* هدر + دکمه افزودن */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>🏢 لیست گمرک‌ها</div>
          <div style={{ fontSize: 12, color: T.subtext, marginTop: 4 }}>مدیریت لیست گمرک‌هایی که در ثبت بارها استفاده می‌شوند</div>
        </div>
        {canAdd && (
          <button onClick={() => setShowAdd(true)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff",
            cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            boxShadow: "0 3px 10px rgba(139,92,246,0.3)",
          }}>
            <Plus size={15} /> گمرک جدید
          </button>
        )}
      </div>

      {/* لیست گمرک‌ها */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
      ) : offices.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 12 }}>
          هیچ گمرکی ثبت نشده
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
          {offices.map((o: any) => (
            <div key={o.id} style={{
              padding: "16px 18px", borderRadius: 12,
              background: dark ? "rgba(255,255,255,0.03)" : "#fff",
              border: `1px solid ${T.cardBorder}`,
              borderRight: "4px solid #8b5cf6",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              {editing?.id === o.id ? (
                <>
                  <input
                    value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    autoFocus
                    style={{
                      flex: 1, padding: "8px 10px", borderRadius: 8,
                      border: `1.5px solid #8b5cf6`,
                      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
                      color: T.text, fontSize: 13, fontFamily: "inherit",
                      direction: "rtl", outline: "none",
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter") submit(handleUpdate);
                      if (e.key === "Escape") setEditing(null);
                    }}
                  />
                  <div style={{ display: "flex", gap: 5, marginRight: 8 }}>
                    <button onClick={() => submit(handleUpdate)} disabled={submitting} style={{ width: 30, height: 30, borderRadius: 7, border: "none", background: "#10b981", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Save size={13} />
                    </button>
                    <button onClick={() => setEditing(null)} style={{ width: 30, height: 30, borderRadius: 7, border: "none", background: T.cardBorder, color: T.subtext, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <X size={13} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Building2 size={18} color="#8b5cf6" />
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{o.name}</span>
                  </div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {canEdit && (
                      <button onClick={() => setEditing({ ...o })} title="ویرایش" style={{ width: 30, height: 30, borderRadius: 7, border: "none", background: "rgba(59,130,246,0.12)", color: "#3b82f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Edit2 size={13} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(o.id, o.name)} title="حذف" style={{ width: 30, height: 30, borderRadius: 7, border: "none", background: "rgba(239,68,68,0.12)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* مودال افزودن */}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={e => { if (e.target === e.currentTarget) { setShowAdd(false); setNewName(""); } }}>
          <div style={{ background: dark ? "#1e293b" : "#fff", borderRadius: 16, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 25px 60px rgba(0,0,0,0.4)" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 17, fontWeight: 800, color: T.text }}>گمرک جدید</h3>
            <label style={{ fontSize: 12, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 6 }}>نام گمرک *</label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
              placeholder="مثلاً: تورخم"
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 10,
                border: `1.5px solid ${T.cardBorder}`,
                background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
                color: T.text, fontSize: 13, fontFamily: "inherit",
                direction: "rtl", outline: "none", boxSizing: "border-box",
              }}
              onKeyDown={e => { if (e.key === "Enter") submit(handleAdd); }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
              <button onClick={() => { setShowAdd(false); setNewName(""); }} style={{ padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
                انصراف
              </button>
              <button onClick={() => submit(handleAdd)} disabled={submitting} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: submitting ? "#94a3b8" : "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff", cursor: submitting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", boxShadow: submitting ? "none" : "0 4px 12px rgba(139,92,246,0.4)" }}>
                {submitting ? "..." : "افزودن"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}